import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Check, ListChecks, Search, Settings2, Trash2, X } from 'lucide-react';
import { MARD_291, PALETTE_BRAND, filterPaletteByCode, readableTextColor } from '../core/palette';

interface Props {
  selectedColor: number | null;
  colors: number[];
  onChange: (colors: number[]) => void;
  onSelect: (index: number) => void;
  onClose: () => void;
}

const MAX_SHORTCUT_COLORS = 36;
type PaletteMode = 'view' | 'edit' | 'manage';

export function ShortcutPaletteModal({ selectedColor, colors, onChange, onSelect, onClose }: Props) {
  const [mode, setMode] = useState<PaletteMode>(colors.length === 0 ? 'edit' : 'view');
  const [query, setQuery] = useState('');
  const [batchSelection, setBatchSelection] = useState<number[]>([]);
  const [deleteRequest, setDeleteRequest] = useState<number[] | null>(null);
  const [feedback, setFeedback] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const longPressTimer = useRef<number | null>(null);
  const longPressStart = useRef<{ x: number; y: number } | null>(null);
  const suppressClick = useRef(false);
  const editing = mode === 'edit';
  const managing = mode === 'manage';
  const candidates = useMemo(() => {
    if (!editing) return [...colors].sort((left, right) => left - right).map((index) => ({ ...MARD_291[index], index })).filter((color) => color.code);
    return filterPaletteByCode(MARD_291.map((color, index) => ({ ...color, index })), query, (color) => color.code);
  }, [colors, editing, query]);

  useEffect(() => {
    if (editing) searchRef.current?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      if (deleteRequest) setDeleteRequest(null);
      else if (mode !== 'view' && colors.length) { setMode('view'); setBatchSelection([]); }
      else onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('keydown', handleKey);
      if (longPressTimer.current !== null) window.clearTimeout(longPressTimer.current);
    };
  }, [colors.length, deleteRequest, editing, mode, onClose]);

  function toggleColor(index: number) {
    if (colors.includes(index)) onChange(colors.filter((item) => item !== index));
    else if (colors.length < MAX_SHORTCUT_COLORS) onChange([...colors, index]);
    setFeedback('');
  }

  function toggleBatchColor(index: number) {
    setBatchSelection((current) => current.includes(index) ? current.filter((item) => item !== index) : [...current, index]);
    setFeedback('');
  }

  function clearLongPressTimer() {
    if (longPressTimer.current !== null) window.clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
    longPressStart.current = null;
  }

  function beginLongPress(event: ReactPointerEvent<HTMLButtonElement>, index: number) {
    if (mode !== 'view' || event.button !== 0) return;
    clearLongPressTimer();
    suppressClick.current = false;
    longPressStart.current = { x: event.clientX, y: event.clientY };
    longPressTimer.current = window.setTimeout(() => {
      suppressClick.current = true;
      longPressTimer.current = null;
      setDeleteRequest([index]);
    }, 550);
  }

  function trackLongPress(event: ReactPointerEvent<HTMLButtonElement>) {
    const start = longPressStart.current;
    if (!start || Math.hypot(event.clientX - start.x, event.clientY - start.y) <= 8) return;
    clearLongPressTimer();
  }

  function handleColorClick(index: number) {
    clearLongPressTimer();
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    if (editing) toggleColor(index);
    else if (managing) toggleBatchColor(index);
    else onSelect(index);
  }

  function confirmDelete() {
    if (!deleteRequest?.length) return;
    const removing = new Set(deleteRequest);
    const next = colors.filter((index) => !removing.has(index));
    onChange(next);
    setFeedback(`已删除 ${deleteRequest.length} 个快捷颜色`);
    setDeleteRequest(null);
    setBatchSelection([]);
    setMode(next.length ? 'view' : 'edit');
  }

  return <div className="quick-palette-layer" onMouseDown={onClose}>
    <section className="quick-palette-dialog shortcut-palette-dialog" role="dialog" aria-modal="true" aria-labelledby="shortcut-palette-title" onMouseDown={(event) => event.stopPropagation()}>
      <header className="quick-palette-head">
        <div><p className="eyebrow">SHORTCUT PALETTE · Q</p><h2 id="shortcut-palette-title">快捷色卡组</h2><span>{editing ? `添加当前图纸常用颜色，最多 ${MAX_SHORTCUT_COLORS} 个；按 MARD 色卡顺序排列。` : managing ? '点选需要删除的颜色，可一次删除多个。' : '当前图纸独立保存；长按颜色可删除，按 Q 随时打开。'}</span></div>
        <div className="shortcut-palette-head-actions">
          <button className={editing ? 'active' : ''} type="button" onClick={() => { setMode(editing && colors.length ? 'view' : 'edit'); setBatchSelection([]); setDeleteRequest(null); }} aria-label={editing ? '完成快捷色卡设置' : '添加快捷颜色'} title={editing ? '完成设置' : '添加颜色'}><Settings2 size={18} /></button>
          {!!colors.length && <button className={managing ? 'active' : ''} type="button" onClick={() => { setMode(managing ? 'view' : 'manage'); setBatchSelection([]); setDeleteRequest(null); }} aria-label={managing ? '退出批量管理' : '批量管理快捷颜色'} title={managing ? '退出批量管理' : '批量管理'}><ListChecks size={18} /></button>}
          <button type="button" onClick={onClose} aria-label="关闭快捷色卡"><X size={19} /></button>
        </div>
      </header>
      {editing && <label className="quick-palette-search"><Search size={17} /><input ref={searchRef} value={query} onChange={(event) => setQuery(event.target.value.replace(/[^a-z0-9]/gi, ''))} aria-label="搜索要加入快捷组的色号" placeholder="搜索色号，例如 G12" inputMode="text" autoCapitalize="characters" autoComplete="off" spellCheck={false} maxLength={16} /><strong>{colors.length}/{MAX_SHORTCUT_COLORS}</strong></label>}
      {!editing && colors.length > 0 && <div className="shortcut-palette-summary"><span>{managing ? `已选择 ${batchSelection.length}/${colors.length} 个颜色` : `已保存 ${colors.length} 个常用颜色 · 长按可删除`}</span><div><button type="button" onClick={() => { setMode('edit'); setBatchSelection([]); }}><Settings2 size={15} />添加颜色</button><button className={managing ? 'active' : ''} type="button" onClick={() => { setMode(managing ? 'view' : 'manage'); setBatchSelection([]); }}><ListChecks size={15} />{managing ? '退出管理' : '批量管理'}</button></div></div>}
      {deleteRequest && <div className="shortcut-delete-confirm" role="alertdialog" aria-modal="false" aria-labelledby="shortcut-delete-title">
        <div><Trash2 size={19} /><span><strong id="shortcut-delete-title">{deleteRequest.length === 1 ? `删除 MARD ${MARD_291[deleteRequest[0]]?.code}？` : `删除选中的 ${deleteRequest.length} 个颜色？`}</strong><small>只会从当前图纸的快捷色卡移除，不会修改图纸内容。</small></span></div>
        <div><button type="button" onClick={() => setDeleteRequest(null)}>取消</button><button className="danger" type="button" onClick={confirmDelete}><Trash2 size={15} />确认删除</button></div>
      </div>}
      {feedback && <div className="shortcut-palette-feedback" role="status">{feedback}</div>}
      <div className="quick-palette-grid shortcut-palette-grid">
        {candidates.map((color) => {
          const inGroup = colors.includes(color.index);
          const batchSelected = batchSelection.includes(color.index);
          const active = editing ? inGroup : managing ? batchSelected : selectedColor === color.index;
          return <button key={color.index} className={`${active ? 'active' : ''} ${managing ? 'manage-item' : ''}`} type="button" onClick={() => handleColorClick(color.index)} onPointerDown={(event) => beginLongPress(event, color.index)} onPointerMove={trackLongPress} onPointerUp={clearLongPressTimer} onPointerCancel={clearLongPressTimer} onPointerLeave={clearLongPressTimer} aria-pressed={editing ? inGroup : managing ? batchSelected : undefined} aria-label={`${editing ? (inGroup ? '移出' : '加入') : managing ? (batchSelected ? '取消选择' : '选择删除') : '选择'} ${PALETTE_BRAND} ${color.code}`} title={`${PALETTE_BRAND} ${color.code}${!editing && !managing ? ' · 长按删除' : ''}`}>
            <i style={{ background: color.hex, color: readableTextColor(color.hex) }}>{color.code}</i>
            <span><small>{PALETTE_BRAND}</small><strong>{color.code}</strong></span>
            {managing ? <b className={`shortcut-batch-check ${batchSelected ? 'checked' : ''}`}>{batchSelected && <Check size={14} />}</b> : active && <Check size={17} />}
          </button>;
        })}
      </div>
      {!candidates.length && <div className="quick-palette-empty">{editing ? '没有找到这个色号' : '还没有设置快捷颜色，请先编辑色卡组。'}</div>}
      {editing && <div className="shortcut-palette-footer"><span>{colors.length >= MAX_SHORTCUT_COLORS ? '已达到快捷颜色上限' : '点击颜色即可加入或移出快捷组'}</span><button className="primary-button" type="button" onClick={() => setMode('view')} disabled={!colors.length}>完成设置</button></div>}
      {managing && <div className="shortcut-palette-footer shortcut-manage-footer"><span>选择一个或多个颜色后统一删除</span><div><button type="button" onClick={() => { setMode('view'); setBatchSelection([]); }}>取消</button><button className="danger-button" type="button" disabled={!batchSelection.length} onClick={() => setDeleteRequest(batchSelection)}><Trash2 size={15} />删除选中</button></div></div>}
    </section>
  </div>;
}
